import React, { forwardRef, useMemo, useEffect } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLCollectedShipmentsQuery } from "store/services/consolidationApi";
import { useLazyGetDDLRouteShipmentsQuery } from "store/services/routeShipmentApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDDLWarehouseListQuery } from "store/services/warehouseApi";
import { useLazyGetDDLWarehousingShipmentsQuery } from "store/services/consolidationApi";
import {
  CONSOLIDATION_LOAD_TYPE,
  CONSOLIDATION_TYPE,
} from "data/static/staticOptions";

// Constants
const FORMATTED_SOURCE_TYPES = [
  { value: "route", label: "Route" },
  { value: "branch", label: "Branch" },
];
const FORMATTED_CONSOLIDATION_MODES = [
  { value: "Source", label: "Source" },
  { value: "In Transit", label: "In Transit" },
];
const INITIAL_STATE = {
  account: "",
  entity: "",
  type: "Origin",
  name: "",
  description: "",
  shipmentId: [],
  loadType: "FTL",
  isActive: true,
  sourceType: "branch",
  route: "",
  branch: "",
  mode: "Source",
  warehouse: "",
};

// Utility to format select options
const formatSelectOptions = (options, isSimpleArray = false) => {
  if (isSimpleArray) return options || [];
  return (
    options?.map((option) => ({
      value: option.value || option._id,
      label: option.label || option.name || option.value || option._id,
    })) || []
  );
};

// Utility to get shipment label
const getShipmentLabel = (shipment) =>
  shipment?.shipmentId
    ? shipment.IsReturning
      ? `${shipment.shipmentId} (R)`
      : `${shipment.shipmentId} - ${shipment.sourceBranch?.name || "Unknown"}`
    : shipment._id;

// Utility to format date for name (YYYYMMDD)
const formatDateForName = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

// Hook to manage data fetching
const useFormDataFetching = ({
  accountId,
  entityId,
  mode,
  sourceType,
  routeId,
  branchId,
  warehouseId,
  fetchEntities,
  fetchShipments,
  fetchWarehousingShipments,
  fetchRouteShipments,
  fetchBranches,
  fetchWarehouses,
  setValue,
}) => {
  // Fetch entities when accountId changes
  useEffect(() => {
    if (accountId) {
      fetchEntities({ account: accountId });
    }
  }, [accountId, fetchEntities]);

  // Fetch branches for both Source and In Transit modes
  useEffect(() => {
    if (accountId && entityId) {
      fetchBranches({ account: accountId, entity: entityId });
    }
  }, [accountId, entityId, fetchBranches]);

  // Fetch warehouses if mode is In Transit
  useEffect(() => {
    if (mode === "In Transit" && accountId && entityId) {
      fetchWarehouses({ account: accountId, entity: entityId });
    }
  }, [mode, accountId, entityId, fetchWarehouses]);

  // Fetch routeShipments if mode is Source and sourceType is route
  useEffect(() => {
    if (mode === "Source" && sourceType === "route" && accountId && entityId) {
      fetchRouteShipments({ account: accountId, entity: entityId });
    }
  }, [mode, sourceType, accountId, entityId, fetchRouteShipments]);

  // Fetch shipments when dependencies change
  useEffect(() => {
    if (
      !accountId ||
      !entityId ||
      (mode === "Source" && !sourceType) ||
      (mode === "Source" && sourceType === "route" && !routeId) ||
      (mode === "Source" && sourceType === "branch" && !branchId) ||
      (mode === "In Transit" && (!warehouseId || !branchId))
    )
      return;
    setValue("shipmentId", []);
    if (mode === "Source") {
      fetchShipments({
        account: accountId,
        entity: entityId,
        routeId: sourceType === "route" ? routeId : undefined,
        branchId: sourceType === "branch" ? branchId : undefined,
        active: "true", // Align with controller's default active filter
      });
    } else if (mode === "In Transit") {
      fetchWarehousingShipments({
        account: accountId,
        entity: entityId,
        warehouseId,
        branchId,
      });
    }
  }, [
    mode,
    accountId,
    entityId,
    sourceType,
    routeId,
    branchId,
    warehouseId,
    fetchShipments,
    fetchWarehousingShipments,
    setValue,
  ]);
};

// Hook to derive route options
const useRouteOptions = (routeShipments) => {
  return useMemo(() => {
    if (!routeShipments || !Array.isArray(routeShipments)) return [];
    return routeShipments
      .filter((item) => item._id && item.routeName)
      .map((item) => ({ _id: item._id, routeName: item.routeName }));
  }, [routeShipments]);
};

const ConsolidationForm = forwardRef(
  ({ isBusy, consolidationDetail, onSubmit }, ref) => {
    const navigate = useNavigate();
    const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

    const {
      control,
      handleSubmit,
      setValue,
      reset,
      formState: { errors },
    } = useForm({
      defaultValues: {
        ...INITIAL_STATE,
        entity: viewEntity?.entity,
        account: viewEntity?.entity?.account,
        sourceType: "branch",
      },
    });

    // Watch form fields
    const [
      watchAccount,
      watchEntity,
      watchMode,
      watchSourceType,
      watchRoute,
      watchBranch,
      watchWarehouse,
    ] = useWatch({
      control,
      name: [
        "account",
        "entity",
        "mode",
        "sourceType",
        "route",
        "branch",
        "warehouse",
      ],
    });

    // Data fetching
    const { data: accounts, isLoading: loadingAccounts } =
      useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
      useLazyGetDDLEntityListQuery();
    const [
      fetchShipments,
      { data: shipments, isLoading: loadingShipments, isError: errorShipments },
    ] = useLazyGetDDLCollectedShipmentsQuery();
    const [
      fetchWarehousingShipments,
      {
        data: warehousingShipments,
        isLoading: loadingWarehousingShipments,
        isError: errorWarehousingShipments,
      },
    ] = useLazyGetDDLWarehousingShipmentsQuery();
    const [
      fetchRouteShipments,
      { data: routeShipments, isLoading: loadingRouteShipments },
    ] = useLazyGetDDLRouteShipmentsQuery();
    const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
      useLazyGetDDLBranchListQuery();
    const [
      fetchWarehouses,
      { data: warehouses, isLoading: loadingWarehouses },
    ] = useLazyGetDDLWarehouseListQuery();

    // Fetch data
    useFormDataFetching({
      accountId: watchAccount?._id,
      entityId: watchEntity?._id,
      mode: watchMode,
      sourceType: watchSourceType,
      routeId: watchRoute?._id,
      branchId: watchBranch?._id,
      warehouseId: watchWarehouse?._id,
      fetchEntities,
      fetchShipments,
      fetchWarehousingShipments,
      fetchRouteShipments,
      fetchBranches,
      fetchWarehouses,
      setValue,
    });

    // Derive route options
    const routeOptions = useRouteOptions(routeShipments);

    // Handle mode and sourceType changes
    useEffect(() => {
      if (watchMode === "Source") {
        setValue("sourceType", "branch");
        setValue("warehouse", "");
        setValue("shipmentId", []);
      } else if (watchMode === "In Transit") {
        setValue("sourceType", "");
        setValue("route", "");
        setValue("branch", "");
        setValue("shipmentId", []);
      }
    }, [watchMode, setValue]);

    // Auto-generate name based on mode, branch, route, warehouse, and date
    useEffect(() => {
      const date = formatDateForName();
      let generatedName = "";

      if (watchMode === "Source") {
        if (watchSourceType === "branch" && watchBranch && watchBranch.name) {
          generatedName = `${watchBranch.name}-${date}`;
        } else if (
          watchSourceType === "route" &&
          watchRoute &&
          watchRoute.routeName
        ) {
          generatedName = `${watchRoute.routeName}-${date}`;
        }
      } else if (
        watchMode === "In Transit" &&
        watchWarehouse &&
        watchWarehouse.name &&
        watchBranch &&
        watchBranch.name
      ) {
        generatedName = `${watchWarehouse.name}-${watchBranch.name}-${date}`;
      }

      setValue("name", generatedName);
    }, [
      watchMode,
      watchSourceType,
      watchRoute,
      watchBranch,
      watchWarehouse,
      setValue,
    ]);

    // Handle form submission
    const handleFormSubmit = (data) => {
      const payload = {
        ...data,
        entity: data.entity?._id,
        account: data.account?._id,
        shipmentId: data.shipmentId?.map((s) => s._id),
        route:
          data.mode === "Source" && data.sourceType === "route"
            ? data.route?._id || null
            : null,
        branch:
          data.mode === "Source" && data.sourceType === "branch"
            ? data.branch?._id || null
            : data.branch?._id || null,
        warehouse:
          data.mode === "In Transit" ? data.warehouse?._id || null : null,
        mode: data.mode,
      };
      onSubmit(payload);
    };

    // Reset form for editing
    useEffect(() => {
      if (!consolidationDetail) return;
      const mode = consolidationDetail.warehouse ? "In Transit" : "Source";
      reset({
        ...consolidationDetail,
        mode,
        sourceType: consolidationDetail.route
          ? "route"
          : consolidationDetail.branch
          ? "branch"
          : "",
        route: consolidationDetail.route || "",
        branch: consolidationDetail.branch || "",
        warehouse: consolidationDetail.warehouse || "",
      });
    }, [consolidationDetail, reset]);

    return (
      <AppForm
        loading={
          isBusy ||
          loadingAccounts ||
          loadingEntities ||
          loadingShipments ||
          loadingWarehousingShipments ||
          loadingRouteShipments ||
          loadingBranches ||
          loadingWarehouses
        }
        ref={ref}
      >
        <AppForm.Content>
          <Grid container spacing={4}>
            {(isAccountAdmin || isSystemAdmin) && (
              <Grid item md={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Account"
                      required
                      value={field.value ?? null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("entity", "");
                        setValue("mode", "Source");
                        setValue("sourceType", "branch");
                        setValue("route", "");
                        setValue("branch", "");
                        setValue("warehouse", "");
                        setValue("shipmentId", []);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled={isAccountAdmin}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>
            )}

            {(isAccountAdmin || isSystemAdmin) && (
              <Grid item md={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      required
                      label="Entity"
                      value={field.value ?? null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("mode", "Source");
                        setValue("sourceType", "branch");
                        setValue("route", "");
                        setValue("branch", "");
                        setValue("warehouse", "");
                        setValue("shipmentId", []);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={6}>
              <Controller
                name="mode"
                control={control}
                rules={{ required: "Please select a consolidation mode." }}
                render={({ field }) => (
                  <BaseSelect
                    {...field}
                    required
                    fullWidth
                    size="small"
                    label="Consolidation Mode"
                    options={FORMATTED_CONSOLIDATION_MODES}
                    getOptionLabel={(option) => option.label || option}
                    getOptionValue={(option) => option.value || option}
                    onChange={(e) => field.onChange(e.target.value)}
                    helperText={errors?.mode?.message}
                    disabled={!!consolidationDetail}
                    error={!!errors?.mode}
                  />
                )}
              />
            </Grid>

            {watchMode === "Source" && (
              <Grid item xs={6}>
                <Controller
                  name="sourceType"
                  control={control}
                  rules={{
                    required:
                      watchMode === "Source"
                        ? "Please select a source type."
                        : false,
                  }}
                  render={({ field }) => (
                    <BaseSelect
                      {...field}
                      required={watchMode === "Source"}
                      fullWidth
                      size="small"
                      label="Source Type"
                      options={FORMATTED_SOURCE_TYPES}
                      getOptionLabel={(option) => option.label || option}
                      getOptionValue={(option) => option.value || option}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValue("route", "");
                        setValue("branch", "");
                        setValue("shipmentId", []);
                      }}
                      helperText={errors?.sourceType?.message}
                      disabled={!!consolidationDetail}
                      error={!!errors?.sourceType}
                    />
                  )}
                />
              </Grid>
            )}

            {watchMode === "Source" && watchSourceType === "route" && (
              <Grid item xs={6}>
                <Controller
                  name="route"
                  control={control}
                  rules={{ required: "Please select a route." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      required
                      label="Route"
                      value={field.value ?? null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("shipmentId", []);
                      }}
                      getOptionLabel={(option) => option?.routeName ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={routeOptions}
                      loading={loadingRouteShipments}
                      loadingText="Loading Routes..."
                      helperText={errors?.route?.message}
                      error={!!errors?.route}
                    />
                  )}
                />
              </Grid>
            )}
            {watchMode === "In Transit" && (
              <Grid item xs={6}>
                <Controller
                  name="warehouse"
                  control={control}
                  rules={{ required: "Please select a warehouse." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      required
                      label="Warehouse"
                      value={field.value ?? null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("shipmentId", []);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={warehouses || []}
                      loading={loadingWarehouses}
                      loadingText="Loading Warehouses..."
                      helperText={errors?.warehouse?.message}
                      error={!!errors?.warehouse}
                    />
                  )}
                />
              </Grid>
            )}

            {(watchMode === "Source" && watchSourceType === "branch") ||
            watchMode === "In Transit" ? (
              <Grid item xs={6}>
                <Controller
                  name="branch"
                  control={control}
                  rules={{ required: "Please select a destination branch." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      required
                      label="Destination Branch"
                      value={field.value ?? null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("shipmentId", []);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={branches || []}
                      loading={loadingBranches}
                      disabled={!!consolidationDetail}
                      loadingText="Loading Branches..."
                      helperText={errors?.branch?.message}
                      error={!!errors?.branch}
                    />
                  )}
                />
              </Grid>
            ) : null}

            <Grid item xs={12} sm={6}>
              <Controller
                name="shipmentId"
                control={control}
                rules={{ required: "Please select at least one Shipment ID." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    multiple
                    label="Shipment ID"
                    required
                    value={value || []}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={getShipmentLabel}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={
                      (watchMode === "Source" &&
                        (loadingShipments ||
                          !watchSourceType ||
                          (!watchRoute && !watchBranch))) ||
                      (watchMode === "In Transit" &&
                        (loadingWarehousingShipments ||
                          !watchWarehouse ||
                          !watchBranch))
                        ? []
                        : watchMode === "Source"
                        ? errorShipments
                          ? []
                          : shipments || []
                        : errorWarehousingShipments
                        ? []
                        : warehousingShipments || []
                    }
                    loading={
                      watchMode === "Source"
                        ? loadingShipments
                        : loadingWarehousingShipments
                    }
                    loadingText="Loading Shipments..."
                    helperText={errors?.shipmentId?.message}
                    error={!!errors?.shipmentId}
                    disabled={
                      !!consolidationDetail ||
                      (watchMode === "Source" && !watchSourceType) ||
                      (watchMode === "Source" &&
                        watchSourceType === "route" &&
                        !watchRoute) ||
                      (watchMode === "Source" &&
                        watchSourceType === "branch" &&
                        !watchBranch) ||
                      (watchMode === "In Transit" &&
                        (!watchWarehouse || !watchBranch))
                    }
                    noOptionsText="No shipments available"
                    key={`${watchMode}-${watchBranch?._id || "no-branch"}-${
                      watchWarehouse?._id || "no-warehouse"
                    }-${watchRoute?._id || "no-route"}`}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    fullWidth
                    size="small"
                    disabled
                    error={!!errors?.name}
                    helperText={errors?.name?.message || "Auto-generated name"}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Type is required." }}
                render={({ field }) => (
                  <BaseSelect
                    {...field}
                    required
                    fullWidth
                    size="small"
                    label="Type"
                    options={formatSelectOptions(CONSOLIDATION_TYPE, "label")}
                    helperText={errors?.type?.message}
                    error={!!errors?.type}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="loadType"
                control={control}
                rules={{ required: "Load Type is required." }}
                render={({ field }) => (
                  <BaseSelect
                    {...field}
                    required
                    fullWidth
                    size="small"
                    label="Load Type"
                    options={formatSelectOptions(
                      CONSOLIDATION_LOAD_TYPE,
                      "label"
                    )}
                    helperText={errors?.loadType?.message}
                    error={!!errors?.loadType}
                  />
                )}
              />
            </Grid>

            <Grid item md={6}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                  />
                )}
              />
            </Grid>

            {consolidationDetail && (
              <Grid item md={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <AppSwitch {...field} label="Is Active?" />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </AppForm.Content>

        <AppForm.Actions>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit(handleFormSubmit)}>
            {consolidationDetail ? "Update" : "Add"}
          </Button>
        </AppForm.Actions>
      </AppForm>
    );
  }
);

// PropTypes for type safety
ConsolidationForm.propTypes = {
  isBusy: PropTypes.bool,
  consolidationDetail: PropTypes.shape({
    account: PropTypes.object,
    entity: PropTypes.object,
    type: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    shipmentId: PropTypes.arrayOf(PropTypes.object),
    loadType: PropTypes.string,
    isActive: PropTypes.bool,
    route: PropTypes.object,
    branch: PropTypes.object,
    mode: PropTypes.string,
    warehouse: PropTypes.object,
  }),
  onSubmit: PropTypes.func,
};

export default ConsolidationForm;
